import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import RunningHeader from "./components/RunningHeader";
import QuestionBankIndex from "@/pages/QuestionBankIndex";
import QuestionBankExam from "@/pages/QuestionBankExam";
import QuestionBankSubject from "@/pages/QuestionBankSubject";
import QuestionBankTopic from "@/pages/QuestionBankTopic";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Evaluate from "@/pages/Evaluate";
import MCQPractice from "@/pages/MCQPractice";
import MockTest from "@/pages/MockTest";
import History from "@/pages/History";
import ProMembership from "@/pages/ProMembership";
import TermsAndConditions from "@/pages/TermsAndConditions";
import RefundPolicy from "@/pages/RefundPolicy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import SyllabusPage from "@/pages/SyllabusNew";
import Admin from "@/pages/Admin";
import Notes from "@/pages/Notes";
import SupportPanel from "@/pages/SupportPanel";
import LMS from "@/pages/LMS";
import LMSManager from "@/pages/LMSManager";
import MyPDFs from "@/pages/MyPDFs";
import NCERTExemplar from "@/pages/NCERTExemplar";
import AppDownload from "@/pages/AppDownload";
import LandingPromo from "@/pages/LandingPromo";
import QuestionBankAdmin from "@/pages/QuestionBankAdmin";
import SubjectSelection, { getSavedSubjectSelection, clearSubjectSelection } from "@/pages/SubjectSelection";
import CategoryPage from "@/pages/CategoryPage";
import CategorySubjectsPage from "@/pages/CategorySubjectsPage";
import SubjectPage from "@/pages/SubjectPageNew";
import FloatingChatBot from "@/components/FloatingChatBot";
import SupportChatAdmin from "@/pages/SupportChatAdmin";
import FloatingEnrollButton from "@/components/FloatingEnrollButton";
import QCDashboard from "@/pages/QCDashboard";
import CleanDuplicates from "@/pages/CleanDuplicates";
import { useState, useEffect } from "react";

// Component to detect and route auth callback
function AppRouter({ user, handleLogin, handleLogout, handleUpgrade, handleProfileUpdate, subjectSelection, onSubjectSelect, onClearSubject, onMediumChange }) {
  const location = useLocation();
  
  // Check if we're on LMS Manager page
  const isLMSManager = location.pathname.startsWith('/lms-manager');
  
  // Hide Help button on MCQ practice, Descriptive test, and QC dashboard
  const isAttemptPage = location.pathname.startsWith('/mcq-practice') || location.pathname.startsWith('/evaluate') || location.pathname.startsWith('/qc');
  
  // Check URL hash for session_id (Google OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.pathname === '/auth/callback' || location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={handleLogin} />;
  }

  // If user is logged in but hasn't selected a subject, show subject selection
  // (except for certain public/admin pages AND new category/subject pages)
  const publicPaths = ['/admin', '/support', '/lms', '/lms-manager', '/terms', '/refund-policy', '/privacy-policy', '/pro', '/select-subject', '/app', '/download', '/enroll', '/join', '/chat', '/category', '/history', '/qc'];
  const isPublicPath = publicPaths.some(p => location.pathname.startsWith(p)) || 
                       location.pathname.startsWith('/syllabus') ||
                       /^\/[a-z_]+\/[a-z_]+/.test(location.pathname); // Matches /tgt/science, /pgt/physics, /non_teaching/jsa etc.
  
  if (user && !subjectSelection && !isPublicPath && location.pathname !== '/') {
    return <Navigate to="/category" />;
  }

  return (
    <>
      <RunningHeader />
      <Routes>
      <Route 
        path="/" 
        element={user ? <Navigate to="/category" replace /> : <Auth onLogin={handleLogin} />}
      />
      <Route 
        path="/auth/callback" 
        element={<AuthCallback onLogin={handleLogin} />} 
      />
      <Route 
        path="/select-subject" 
        element={user ? <Navigate to="/category" replace /> : <Navigate to="/" />} 
      />
      <Route 
        path="/dashboard" 
        element={<Navigate to="/category" replace />} 
      />
      <Route 
        path="/evaluate" 
        element={user ? (subjectSelection ? <Evaluate user={user} onLogout={handleLogout} subjectSelection={subjectSelection} /> : <Navigate to="/category" />) : <Navigate to="/" />} 
      />
      <Route 
        path="/mcq-practice" 
        element={user ? (subjectSelection ? <MCQPractice user={user} subjectSelection={subjectSelection} onLogin={handleLogin} /> : <Navigate to="/category" />) : <Navigate to="/" />} 
      />
      <Route 
        path="/mock-test" 
        element={user ? (subjectSelection ? <MockTest user={user} subjectSelection={subjectSelection} /> : <Navigate to="/category" />) : <Navigate to="/" />} 
      />
      <Route 
        path="/history" 
        element={user ? <History user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
      />
      <Route 
        path="/notes" 
        element={user ? (subjectSelection ? <Notes user={user} subjectSelection={subjectSelection} onLogin={handleLogin} /> : <Navigate to="/category" />) : <Navigate to="/" />} 
      />
      <Route 
        path="/ncert-exemplar" 
        element={user ? (subjectSelection ? <NCERTExemplar user={user} subjectSelection={subjectSelection} /> : <Navigate to="/category" />) : <Navigate to="/" />} 
      />
      <Route 
        path="/my-pdfs" 
        element={user ? (subjectSelection ? <MyPDFs user={user} subjectSelection={subjectSelection} /> : <Navigate to="/category" />) : <Navigate to="/" />} 
      />
      <Route 
        path="/pro" 
        element={user ? <ProMembership user={user} onUpgrade={handleUpgrade} /> : <Navigate to="/" />} 
      />
      <Route 
        path="/terms" 
        element={<TermsAndConditions />} 
      />
      <Route 
        path="/refund-policy" 
        element={<RefundPolicy />} 
      />
      <Route 
        path="/privacy-policy" 
        element={<PrivacyPolicy />} 
      />
      
      {/* New Category & Subject Pages (Public - No auth required) */}
      <Route 
        path="/category" 
        element={<CategoryPage />} 
      />
      <Route 
        path="/category/:categoryId" 
        element={<CategorySubjectsPage />} 
      />
      <Route 
        path="/:examType/:subject" 
        element={<SubjectPage onSubjectSelect={onSubjectSelect} />} 
      />
      
      {/* Admin Panel - Password Protected */}
      <Route path="/admin" element={<Admin />} />
      <Route path="/support" element={<SupportPanel />} />
      <Route path="/lms" element={<LMS />} />
      <Route path="/lms-manager" element={<LMSManager />} />
      {/* Public Landing / Promo Enrollment Page */}
      <Route path="/enroll" element={<LandingPromo onLogin={handleLogin} />} />
      <Route path="/chat" element={<SupportChatAdmin />} />
      <Route path="/join" element={<LandingPromo onLogin={handleLogin} />} />
      {/* App Download Page - Public */}
      <Route path="/app" element={<AppDownload />} />
      <Route path="/download" element={<AppDownload />} />
      {/* Question Bank Admin - Password protected standalone page */}
      <Route path="/questionbank" element={<QuestionBankAdmin />} />
      {/* QC Dashboard - Azure OpenAI powered quality check */}
      <Route path="/qc" element={<QCDashboard />} />
      <Route path="/clean" element={<CleanDuplicates />} />
      {/* Syllabus Pages - Public (no login required for SEO) */}
      <Route path="/syllabus/:type/:subject" element={<SyllabusPage />} />
      <Route path="/syllabus/:subject" element={<Navigate to="/syllabus/tgt/hindi" />} />
      <Route path="/syllabus" element={<Navigate to="/syllabus/tgt/hindi" />} />
      {/* Public Question Bank Pages - SEO optimized */}
      <Route path="/question-bank" element={<QuestionBankIndex />} />
      <Route path="/question-bank/:examType" element={<QuestionBankExam />} />
      <Route path="/question-bank/:examType/:subject" element={<QuestionBankSubject />} />
      <Route path="/question-bank/:examType/:subject/:topicSlug" element={<QuestionBankTopic />} />
    </Routes>
    
    {/* Floating Chatbot - Hide on LMS Manager and MCQ/Evaluate attempt pages */}
    {!isLMSManager && !isAttemptPage && <FloatingChatBot user={user} />}
    {/* Floating Enroll button - only for free / non-paid users */}
    {!isLMSManager && !isAttemptPage && <FloatingEnrollButton user={user} />}
    </>
  );
}

function App() {
  // Initialize user from localStorage synchronously to prevent redirect issues
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });
  
  // Initialize subject selection from localStorage
  const [subjectSelection, setSubjectSelection] = useState(() => {
    return getSavedSubjectSelection();
  });
  
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Re-validate user from localStorage on mount (for updates)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    }
    
    // Also refresh subject selection
    setSubjectSelection(getSavedSubjectSelection());
  }, []);

  // Session validation: check every 30 seconds if this window's session is still valid
  useEffect(() => {
    if (!user) return;
    
    const checkSession = async () => {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken || !user?.id) return;
      
      try {
        const API = `${window.location.origin}/api`;
        const res = await fetch(`${API}/auth/validate-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.id, session_token: sessionToken })
        });
        const data = await res.json();
        
        if (!data.valid && data.reason === 'session_expired') {
          // Only logout when session was explicitly expired (another device logged in)
          handleLogout();
          window.alert('You have been logged out because your account is active on another device. Only 1 device allowed at a time.');
        }
      } catch {
        // Network error — don't logout, just skip this check
      }
    };

    // Check immediately, then every 30 seconds
    checkSession();
    const interval = setInterval(checkSession, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('session_token');
    // Also clear subject selection on logout
    clearSubjectSelection();
    setSubjectSelection(null);
  };

  const handleUpgrade = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleProfileUpdate = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };
  
  const handleSubjectSelect = (selection) => {
    setSubjectSelection(selection);
  };
  
  const handleClearSubject = () => {
    clearSubjectSelection();
    setSubjectSelection(null);
  };

  const handleMediumChange = (language) => {
    if (!subjectSelection) return;
    const updated = { ...subjectSelection, language };
    localStorage.setItem('user_subject_selection', JSON.stringify(updated));
    setSubjectSelection(updated);
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        {/* Maintenance Banner removed - site is stable */}
        
        <div className="pt-0">
          <AppRouter 
            user={user} 
            handleLogin={handleLogin} 
            handleLogout={handleLogout} 
            handleUpgrade={handleUpgrade}
            handleProfileUpdate={handleProfileUpdate}
            subjectSelection={subjectSelection}
            onSubjectSelect={handleSubjectSelect}
            onClearSubject={handleClearSubject}
            onMediumChange={handleMediumChange}
          />
        </div>
      </BrowserRouter>
      </HelmetProvider>
      <Toaster richColors />
    </div>
  );
}

export default App;